import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ENVIRONMENT_INITIALIZER, inject } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, NativeDateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideFuse } from '@fuse';
import { appRoutes } from 'app/app.routes';
import { NGX_TIMEPICKER_THEME, timepickerTheme } from 'app/core/config/timepicker.config';
import { provideIcons } from 'app/core/icons/icons.provider';
import { environment } from 'environments/environment';
import { RECAPTCHA_SETTINGS, RecaptchaSettings } from 'ng-recaptcha';
import { baseInterceptor } from './core/interceptors/base.interceptor';
import { AutoCellTooltipService } from './core/services/auto-cell-tooltip.service';
import { AutoPlaceholderService } from './core/services/auto-placeholder.service';
import { DateReadonlyService } from './core/services/date-readonly.service';
import { GlobalClearableService } from './core/services/global-clearable.service';
import { MatOptionLabelService } from './core/services/mat-option-label.service';
import { MatSelectChipsService } from './core/services/mat-select-chips.service';
import { MatSelectSearchService } from './core/services/mat-select-search.service';
import { NumberWheelGuardService } from './core/services/number-wheel-guard.service';
import { StickyTableHeaderService } from './core/services/sticky-table-header.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(withInterceptors([baseInterceptor])),
    provideRouter(appRoutes, withPreloading(PreloadAllModules), withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    {
      provide: DateAdapter,
      useClass: NativeDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: null,
        },
        display: {
          dateInput: { year: 'numeric', month: 'long', day: 'numeric' },
          monthYearLabel: { year: 'numeric', month: 'short' },
          dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
          monthYearA11yLabel: { year: 'numeric', month: 'long' },
        },
      },
    },
    // Material form field defaults: dynamic subscript + always float label for visible placeholders
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic', floatLabel: 'always' },
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { disableClose: true, autoFocus: false, restoreFocus: true },
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'app-select-panel' },
    },
    // Auto-tooltip on truncated description/note/memo cells across all tables
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(AutoCellTooltipService).init(),
    },
    // Auto-inject a search input into every mat-select panel on open
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(MatSelectSearchService).init(),
    },
    // Render selected values of every multi mat-select as chips inside the field
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(MatSelectChipsService).init(),
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(MatOptionLabelService).init(),
    },
    // Make all date inputs readonly — picker-only selection, clicking anywhere in the field opens the calendar
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(DateReadonlyService).init(),
    },
    // Auto-inject clear button into every mat-form-field text input
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(GlobalClearableService).init(),
    },
    // Auto-set placeholder from mat-label on every matInput that has no explicit placeholder
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(AutoPlaceholderService).init(),
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(NumberWheelGuardService).init(),
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(StickyTableHeaderService).init(),
    },
    { provide: NGX_TIMEPICKER_THEME, useValue: timepickerTheme },
    // reCAPTCHA v2 invisible site key
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: { siteKey: (environment as any).recaptchaSiteKey ?? '' } as RecaptchaSettings,
    },
    // Fuse
    provideIcons(),
    provideFuse({
      fuse: {
        layout: 'classy',
        scheme: 'light',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-default',
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
        ],
      },
    }),
  ],
};
